import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Editor } from "primereact/editor";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import MeaButton from "../../../../components/MeaButton";
import studentsAPI from "../../../../services/students";
import schoolsAPI from "../../../../services/schools";

const extractEmails = (student) => {
  const emails = [];
  const seen = new Set();
  const pushEmail = (value) => {
    if (!value) {
      return;
    }
    const normalized = value.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    emails.push(value.trim());
  };

  pushEmail(student?.email);
  pushEmail(student?.member?.email);
  pushEmail(student?.father_email);
  pushEmail(student?.father?.email);
  pushEmail(student?.mother_email);
  pushEmail(student?.mother?.email);

  return emails;
};

const ContactStudentMailModal = forwardRef(({ schoolId, trainingId, student }, ref) => {
  const toast = useRef();
  const [visible, setVisible] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState("");
  const [loadingSchool, setLoadingSchool] = useState(false);
  const recipients = useMemo(() => extractEmails(student), [student]);

  const loadSchoolEmail = () => {
    setLoadingSchool(true);
    schoolsAPI
      .GetOneSchool(schoolId)
      .then((result) => {
        setReplyTo(result?.data?.email || "");
      })
      .catch(() => {
        setReplyTo("");
      })
      .finally(() => setLoadingSchool(false));
  };

  useImperativeHandle(ref, () => ({
    show: () => {
      setSubject("");
      setBody("");
      loadSchoolEmail();
      setVisible(true);
    },
    hide: () => {
      setVisible(false);
    },
  }));

  const handleSend = () => {
    const plainBody = (body || "").replace(/<[^>]*>/g, "").trim();
    if (!subject.trim() || !plainBody || !recipients.length || !replyTo) {
      return;
    }

    setSending(true);
    studentsAPI
      .SendContactEmail(schoolId, trainingId, "", student?.id, {
        subject: subject.trim(),
        body,
      })
      .then((result) => {
        toast.current.show({
          severity: "success",
          summary: "Succès",
          detail: result?.message || "Le mail a été envoyé avec succès.",
        });
        setVisible(false);
      })
      .catch((err) => {
        toast.current.show({
          severity: "error",
          summary: "Erreur",
          detail: err.message,
        });
      })
      .finally(() => setSending(false));
  };

  const canSend =
    subject.trim().length > 0 &&
    (body || "").replace(/<[^>]*>/g, "").trim().length > 0 &&
    recipients.length > 0 &&
    !!replyTo &&
    !loadingSchool &&
    !sending;

  return (
    <>
      <Toast ref={toast} position="bottom-center" />
      <Dialog
        header="Contacter l'élève et les parents"
        visible={visible}
        style={{ width: "55vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
        modal
        onHide={() => {
          if (!sending) {
            setVisible(false);
          }
        }}
        footer={
          <div className="flex justify-content-end gap-2">
            <MeaButton
              label="Annuler"
              icon="pi pi-times"
              className="p-button-text"
              disabled={sending}
              onClick={() => setVisible(false)}
            />
            <MeaButton
              label="Envoyer"
              icon="pi pi-send"
              loading={sending}
              disabled={!canSend}
              onClick={handleSend}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <div className="surface-50 border-1 border-200 border-round p-3">
            <div className="text-sm text-600 mb-2">Destinataires</div>
            <div className="flex flex-wrap gap-2">
              {recipients.length ? (
                recipients.map((email) => <Tag key={email} value={email} />)
              ) : (
                <Tag value="Aucune adresse trouvée" severity="warning" />
              )}
            </div>
            <div className="mt-3 text-sm">
              <span className="text-600">Reply-to : </span>
              <span className="font-medium">
                {loadingSchool
                  ? "Chargement..."
                  : replyTo || "Email de l'école non renseigné dans la fiche école"}
              </span>
            </div>
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="contact-subject" className="font-medium">
              Objet
            </label>
            <InputText
              id="contact-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet du mail"
              className="w-full"
            />
          </div>

          <div className="flex flex-column gap-2">
            <label htmlFor="contact-body" className="font-medium">
              Contenu
            </label>
            <Editor
              id="contact-body"
              value={body}
              onTextChange={(e) => setBody(e.htmlValue)}
              style={{ height: "320px" }}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
});

export default ContactStudentMailModal;
